import type { EventCategory, VisualizerLayer, VisualizerType } from '../domain/schemas';

/**
 * Default visualizer layers (Phase 4C): calm, moderate settings suitable for modest
 * hardware (48 bars, 120 particles, no flash). Every layer is centred in a scene of the
 * given size.
 */

export const VISUALIZER_LABELS: Record<VisualizerType, string> = {
  bars: 'Frequency bars',
  radial: 'Radial spectrum',
  waveform: 'Waveform',
  pulse: 'Beat pulse',
  particles: 'Particle field',
  grid: 'Retro grid',
};

export const VISUALIZER_DESCRIPTIONS: Record<VisualizerType, string> = {
  bars: 'Bars along a baseline; heights follow the frequency spectrum.',
  radial: 'Bars around a circle; rotates with the tempo.',
  waveform: 'The audio waveform as a line or filled shape.',
  pulse: 'Expanding rings or shapes on kicks and snares.',
  particles: 'Lightweight particles emitted on beats (seeded, deterministic).',
  grid: 'A perspective horizon grid scrolling with the tempo.',
};

type Size = { width: number; height: number };

const DEFAULT_CATEGORIES: Record<VisualizerType, EventCategory[]> = {
  bars: ['kick', 'bass808', 'snare', 'clap', 'hats', 'perc'],
  radial: ['kick', 'bass808', 'snare', 'clap', 'hats', 'perc'],
  waveform: ['kick', 'bass808', 'snare', 'clap', 'hats'],
  pulse: ['kick'],
  particles: ['kick', 'snare'],
  grid: ['beat'],
};

/** A new layer of `type` with sensible defaults (id and seed supplied by the caller). */
export function defaultLayer(type: VisualizerType, scene: Size, id: string, seed = 1): VisualizerLayer {
  const W = scene.width;
  const H = scene.height;
  const base = {
    id,
    name: VISUALIZER_LABELS[type],
    visible: true,
    placement: 'behind' as const,
    blend: 'normal' as const,
    x: W / 2,
    y: H / 2,
    width: Math.min(4000, Math.round(W * 0.8)),
    height: Math.min(4000, Math.round(H * 0.5)),
    scale: 1,
    rotation: 0,
    opacity: 1,
    style: { primary: '#a855f7', secondary: '#ec4899', glow: 0.3 },
    response: { source: 'measured' as const, band: 'full' as const, categories: DEFAULT_CATEGORIES[type], sensitivity: 1, smoothing: 0.3, decay: 0.25 },
    seed,
  };
  switch (type) {
    case 'bars':
      return { ...base, type, y: H * 0.7, params: { orientation: 'horizontal', count: 48, gap: 0.25, mirrored: false, minHeight: 0.02 } };
    case 'radial': {
      const side = Math.round(Math.min(W, H) * 0.7);
      return { ...base, type, width: side, height: side, params: { count: 72, innerRadius: 0.45, length: 0.5, direction: 'outward', spin: 0.25, symmetry: 1, barWidth: 0.5 } };
    }
    case 'waveform':
      return { ...base, type, height: Math.round(H * 0.3), params: { thickness: 3, filled: false, mirrored: false, windowMs: 60, points: 192 } };
    case 'pulse': {
      const side = Math.round(Math.min(W, H) * 0.8);
      return { ...base, type, width: side, height: side, response: { ...base.response, source: 'events', band: 'low', decay: 0.6 }, params: { shape: 'ring', maxRings: 4, expand: 1, lineWidth: 4, flash: 0, core: 0.2 } };
    }
    case 'particles':
      return {
        ...base,
        type,
        height: Math.round(H * 0.8),
        response: { ...base.response, source: 'events', band: 'low', decay: 0.3 },
        params: { count: 120, perEvent: 12, rate: 0, lifetime: 1.5, speed: 220, spread: 120, direction: -90, gravity: 120, size: 3 },
      };
    case 'grid':
      return {
        ...base,
        type,
        y: H * 0.75,
        height: Math.round(H * 0.5),
        width: W,
        response: { ...base.response, source: 'events', band: 'low', decay: 0.3 },
        params: { lines: 14, speed: 1, horizon: 0.1, pulse: 0.5 },
      };
  }
}
